import { Controller } from '@hotwired/stimulus';
import { nextOccurrenceRound, offsetInTrack, orderedCharacters, presentationCard, syncChildren, visibleRoundSlices } from '../../lib/character_list_2.js';
import { animateCardAppearance, captureCardAppearance, TURN_DURATION } from '../../lib/character_list_2_appearance.js';
import { animateEntry, animateExit, captureExit } from '../../lib/character_list_2_visibility.js';
import { hasPositionChange, positionFrames, remainingPositionStages } from '../../lib/character_list_2_positions.js';
import { animateScroll } from '../../lib/character_list_2_scroll.js';
import { waitForRoundAnimation } from '../../lib/round_animation.js';

const MOTION_ELEMENTS = '.character-list-2__slot:not(.is-hidden), .character-list-2__round-divider';

// The server owns the current roster. Past/future cards are a small, passive
// window onto that same roster, with spacers retaining the timeline's geometry.
export default class extends Controller {
  static targets = ['viewport', 'track', 'source', 'past', 'future', 'returnToTurn', 'currentRound'];
  static values = { combat: Boolean, round: Number, preview: Boolean };

  connect() {
    this.connected = true;
    this.positionAnimations = new Map();
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.motionChanged = () => {
      if (this.motion.matches) {
        this.cancelPositionAnimations();
        if (this.centerRequest || this.pendingCenter || this.cancelScroll) this.centerActive();
      }
    };
    this.motion.addEventListener('change', this.motionChanged);
    this.sourceObserver = new MutationObserver(() => this.refresh());
    this.sourceObserver.observe(this.sourceTarget, {
      subtree: true, childList: true, characterData: true,
      attributes: true, attributeFilter: ['class', 'src', 'data-position']
    });
    this.resizeObserver = new ResizeObserver(() => this.refresh());
    this.resizeObserver.observe(this.viewportTarget);
    this.refresh();
  }

  combatValueChanged() { this.refresh(); }
  roundValueChanged() { this.refresh(); }

  beforeMorph(event) {
    if (!this.connected) return;
    if (!this.previewValue && !this.motion.matches &&
        (event.target === this.element || event.target === this.sourceTarget || this.sourceTarget.contains(event.target))) {
      this.beforeVisible ||= new Set(this.characters.map(character => character.dataset.characterId));
      if (event.target.matches?.('[data-character-list-2-card]') && !event.target.classList.contains('is-hidden') &&
          (!event.detail?.newElement || event.detail.newElement.classList.contains('is-hidden'))) {
        this.exits ||= new Map();
        for (const slot of this.trackTarget.querySelectorAll('.character-list-2__slot:not(.is-hidden)')) {
          if (slot.dataset.characterId !== event.target.dataset.characterId) continue;
          const key = slot.closest('[data-round]').dataset.round + ':' + slot.dataset.characterId;
          if ((!this.positioned && !this.visibleSlot(slot)) || (this.positioned && slot !== event.target)) continue;
          if (!this.exits.has(key)) this.exits.set(key, captureExit(slot, this.trackTarget, this.scrollLeft));
        }
      }
    }
    // This presentation flag belongs to the timeline, not the server's roster.
    if (event.target.matches?.('[data-character-list-2-card]') && event.detail?.newElement) {
      event.detail.newElement.classList.toggle('is-muted', event.target.classList.contains('is-muted'));
    }
    if (this.cancelScroll) {
      this.stopScrolling();
      this.pendingCenter = true;
    }
    if (this.anchor) return;
    if (event.target === this.element || event.target === this.sourceTarget || this.sourceTarget.contains(event.target)) {
      this.anchor = this.captureAnchor();
      this.beforePositions ||= this.capturePositions();
      this.beforeAppearances ||= this.captureAppearances();
    }
  }

  refresh(event) {
    if (!this.connected) return;
    cancelAnimationFrame(this.frame);
    if (event?.type === 'turbo:morph-element') {
      if (this.morphRequest) return;
      const request = this.morphRequest = {};
      // Turbo can morph inside a render frame. Waiting for another frame would
      // paint the new slot widths once before their visual positions are held.
      // Batch the completed synchronous morph in a microtask before that paint.
      queueMicrotask(() => {
        if (!this.connected || this.morphRequest !== request) return;
        this.morphRequest = null;
        cancelAnimationFrame(this.frame);
        this.reconcile();
      });
      return;
    }
    if (this.morphRequest) return;
    this.frame = requestAnimationFrame(() => this.reconcile());
  }

  get characters() {
    return orderedCharacters(this.sourceTarget.querySelectorAll(':scope > [data-character-list-2-card]'));
  }

  get activeCharacter() {
    return this.characters.find(character => character.classList.contains('is-active'));
  }

  get positioned() {
    return this.viewportTarget.dataset.characterList2ScrollLeft !== undefined;
  }

  get scrollLeft() {
    return this.positioned ? Number(this.viewportTarget.dataset.characterList2ScrollLeft) : this.viewportTarget.scrollLeft;
  }

  set scrollLeft(value) {
    const left = Math.max(0, value);
    if (this.positioned) {
      if (Number(this.viewportTarget.dataset.characterList2ScrollLeft) === left) return;
      this.viewportTarget.dataset.characterList2ScrollLeft = String(left);
      this.trackTarget.style.translate = `${-left}px 0`;
    } else if (Math.abs(this.viewportTarget.scrollLeft - left) > 0.5) {
      // Reassigning even the same scrollLeft interrupts native smooth scrolling.
      this.viewportTarget.scrollLeft = left;
    }
  }

  reconcile() {
    if (!this.connected) return;
    this.measureGeometry();
    const characters = this.characters;
    const activeIndex = characters.findIndex(character => character.classList.contains('is-active'));
    const activeCharacter = characters[activeIndex];
    const records = characters.filter(character => !character.hasAttribute('data-character-list-2-reveal-phase'));
    const geometry = `${this.element.clientWidth || this.viewportTarget.clientWidth}:${this.viewportTarget.clientHeight}:${this.element.className}`;
    const state = records.map(character => [character.dataset.characterId, character.dataset.position,
      character.className.replace(/\bis-muted\b/g, '').trim(), character.querySelector('.character-list-2__card').innerHTML]);
    const key = JSON.stringify([this.combatValue, this.roundValue, geometry, state]);
    const anchor = this.anchor || this.captureAnchor();
    this.anchor = null;
    const beforePositions = this.beforePositions;
    const beforeAppearances = this.beforeAppearances;
    const beforeVisible = this.beforeVisible;
    const exits = this.exits;
    this.beforePositions = null;
    this.beforeAppearances = null;
    this.beforeVisible = null;
    this.exits = null;
    if (key === this.stateKey) {
      if (this.pendingCenter) this.centerActive();
      this.updateRoundContext();
      this.updateReturnButton();
      return;
    }
    const initial = this.stateKey === undefined;
    const resized = geometry !== this.geometry;
    this.stateKey = key;
    this.geometry = geometry;
    const turn = this.combatValue ? `${this.roundValue}:${activeCharacter?.dataset.characterId || ''}` : null;
    const turnChanged = turn !== this.turn;
    this.turn = turn;
    this.revision = (this.revision || 0) + 1;
    this.records = records;
    const entering = beforeVisible ? records.filter(character => !beforeVisible.has(character.dataset.characterId)) : [];
    // The active character can be presenting outside the rail. Its insertion
    // point still separates completed turns from upcoming ones during that reveal.
    this.activeIndex = activeIndex < 0 ? -1 : characters.slice(0, activeIndex)
      .filter(character => !character.hasAttribute('data-character-list-2-reveal-phase')).length;
    this.updateOccurrences();
    this.templates = new Map();
    this.groups ||= new Map();
    this.spacers ||= new Map();

    if (this.combatValue && records.length) {
      this.originRound = this.originRound === undefined || this.roundValue < this.originRound
        ? Math.max(1, this.roundValue - 1) : this.originRound;
      // Round 1 opens at the center with breathing room, not a fictitious round0.
      if (this.roundValue === 1 && !this.renderedCombat) this.originRound = 1;
      this.measureRound();
      this.leadingWidth = Math.max(0, this.viewportTarget.clientWidth / 2 - this.cardStart - this.stride / 2);
      this.prefixWidth = this.leadingWidth + (this.roundValue - this.originRound) * this.roundWidth;
      this.sourceWidth = this.sourceTarget.offsetWidth;
      this.windowKey = null;
      const restored = this.anchorPosition(anchor);
      this.renderWindow(restored ?? this.scrollLeft);
      if (!initial && restored !== null) this.scrollLeft = restored;
    } else {
      this.pastTarget.replaceChildren();
      this.futureTarget.replaceChildren();
      this.groups.clear();
      this.roundWidth = 0;
      this.prefixWidth = 0;
      this.originRound = undefined;
      if (!turnChanged && anchor) this.restoreAnchor(anchor);
    }
    this.renderedCombat = this.combatValue;
    this.animatePositions(beforePositions, turnChanged, beforeAppearances, { entering, exits });
    this.animateVisibility(entering, exits);
    if (turnChanged || resized || this.pendingCenter) this.centerActive(initial);
    this.updateRoundContext();
    this.updateReturnButton();
    this.dispatch('updated');
  }

  updateOccurrences() {
    this.records.forEach((character, index) => {
      character.classList.toggle('is-muted', this.combatValue &&
        this.roundValue !== nextOccurrenceRound(index, this.activeIndex, this.roundValue));
    });
  }

  capturePositions() {
    if (this.previewValue || this.motion.matches || this.positioned) return null;
    const positions = new Map();
    positions.motions = new Map();
    positions.panels = new Map();
    const left = this.scrollLeft;
    const margin = this.stride || 0;
    for (const slot of this.trackTarget.querySelectorAll(MOTION_ELEMENTS)) {
      if (slot.hasAttribute('data-character-list-2-reveal-phase')) continue;
      const transform = getComputedStyle(slot).transform;
      const translation = transform === 'none' ? 0 : new DOMMatrixReadOnly(transform).m41;
      const x = offsetInTrack(slot, this.trackTarget) + translation - left;
      if (x + slot.offsetWidth < -margin || x > this.viewportTarget.clientWidth + margin) continue;
      const key = `${slot.closest('[data-round]').dataset.round}:${slot.dataset.characterId || 'divider'}`;
      positions.set(key, x);
      const exposed = this.panelExposure(slot);
      if (exposed > 0.5) positions.panels.set(key, { left: x, width: exposed });
      const animation = this.positionAnimations?.get(slot);
      if (animation?.characterList2Positions) {
        positions.motions.set(key, remainingPositionStages(animation.characterList2Positions,
          Number(animation.currentTime) || 0, translation));
      }
    }
    return positions;
  }

  panelExposure(slot) {
    const panel = slot.querySelector?.('.character-list-2__status-panel');
    if (!panel) return 0;
    const transform = getComputedStyle(panel).transform;
    if (!transform || transform === 'none') return 0;
    const matrix = new DOMMatrixReadOnly(transform);
    // Quarter turns slide on the local Y axis, which becomes physical X.
    return Math.hypot(matrix.m41, matrix.m42);
  }

  panelFootprint(slot) {
    const card = slot.querySelector?.('.character-list-2__card');
    if (!card) return 0;
    const quarterTurn = /character-list-2--rotation-(ninety|two-seventy)/.test(this.element.className);
    return Math.max(0, slot.offsetWidth - (quarterTurn ? card.offsetHeight : card.offsetWidth));
  }

  measureGeometry() {
    // Resolve the responsive CSS gap again instead of reading our previous
    // pixel override after a resize or a change in screen placement.
    this.element.style.removeProperty('--character-list-2-measured-gap');
    this.element.style.removeProperty('--character-list-2-measured-panel-gap');
    const viewport = getComputedStyle(this.viewportTarget);
    const height = this.viewportTarget.clientHeight - parseFloat(viewport.paddingTop) - parseFloat(viewport.paddingBottom);
    const gap = parseFloat(getComputedStyle(this.sourceTarget).columnGap);
    for (const [name, value] of [['height', height], ['gap', gap], ['panel-gap', gap]]) {
      if (Number.isFinite(value) && value > 0) this.element.style.setProperty('--character-list-2-measured-' + name, value + 'px');
    }
  }

  captureAppearances() {
    if (!this.combatValue || this.previewValue || this.motion.matches || this.positioned) return null;
    const appearances = new Map();
    for (const slot of this.trackTarget.querySelectorAll('.character-list-2__slot:not(.is-hidden)')) {
      if (slot.hasAttribute('data-character-list-2-reveal-phase')) continue;
      const key = `${slot.closest('[data-round]').dataset.round}:${slot.dataset.characterId}`;
      if (!this.beforePositions?.has(key)) continue;
      const running = ['card', 'face', 'avatar', 'status-panel', 'condition-icons'].map(part =>
        this.positionAnimations?.get(slot.querySelector(part === 'avatar' ? '.avatar' : '.character-list-2__' + part)))
        .find(animation => animation?.characterList2Appearance);
      appearances.set(key, captureCardAppearance(slot, running));
    }
    return appearances;
  }

  animatePositions(before, waitForTurn, appearances, { entering = [], exits } = {}) {
    if (this.previewValue || this.positioned || this.motion.matches ||
        this.records.some(character => character.classList.contains('is-focused'))) {
      this.cancelPositionAnimations();
      return;
    }
    // Portrait loading or other content-only updates must not interrupt a turn
    // animation already in flight. A new morph supplies its own visual snapshot.
    if (!before?.size) {
      this.animateAppearances(appearances, waitForTurn);
      return;
    }
    this.cancelPositionAnimations();
    this.positionAnimations ||= new Map();
    const active = this.records.find(character => character.classList.contains('is-active') &&
      !character.hasAttribute('data-character-list-2-reveal-phase'));
    const activeLeft = active ? offsetInTrack(active, this.trackTarget) - this.scrollLeft : Infinity;
    const activeKey = active && `${active.closest('[data-round]').dataset.round}:${active.dataset.characterId}`;
    const openingWidth = active ? Math.max(0, this.panelFootprint(active) - (before.panels?.get(activeKey)?.width || 0)) : 0;
    for (const slot of this.trackTarget.querySelectorAll(MOTION_ELEMENTS)) {
      if (slot.hasAttribute('data-character-list-2-reveal-phase')) continue;
      const key = `${slot.closest('[data-round]').dataset.round}:${slot.dataset.characterId || 'divider'}`;
      const previous = before.get(key);
      if (previous === undefined) continue;
      const current = offsetInTrack(slot, this.trackTarget) - this.scrollLeft;
      const delta = previous - current;
      const stages = waitForTurn ? {
        closing: [...before.panels || []].reduce((width, [panelKey, panel]) =>
          width + (panelKey !== activeKey && previous > panel.left + 0.5 ? panel.width : 0), 0),
        opening: current > activeLeft + 0.5 ? openingWidth : 0,
        elapsed: 0
      } : before.motions?.get(key);
      // A card beyond both old and new active turns can end at the same spot,
      // yet still needs to follow the closing tile and then the opening tile.
      if (!hasPositionChange(delta, stages)) continue;
      if (stages) {
        const animation = slot.animate(positionFrames(delta, stages), {
          duration: Math.max(1, TURN_DURATION - stages.elapsed), fill: 'both'
        });
        animation.characterList2Positions = { delta, ...stages };
        this.trackAnimation(slot, animation, waitForTurn);
        continue;
      }
      const duration = this.landedCharacter ? 240 : exits?.size ? 380 : entering.length ? 260 : 550;
      const hold = exits?.size ? 160 : 0;
      // Move slots and dividers together, leaving the portrait's independent
      // focus/rotation transforms intact. Measure final layout before painting.
      const animation = slot.animate([
        { transform: `translateX(${delta}px)` },
        { transform: `translateX(${delta}px)`, offset: hold / duration,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
        { transform: 'translateX(0px)' }
      ], { duration, fill: 'both' });
      this.trackAnimation(slot, animation, waitForTurn);
    }
    this.animateAppearances(appearances, waitForTurn);
  }

  animateAppearances(before, waitForTurn) {
    if (!before?.size) return;
    for (const slot of this.trackTarget.querySelectorAll('.character-list-2__slot:not(.is-hidden)')) {
      if (slot.hasAttribute('data-character-list-2-reveal-phase')) continue;
      const previous = before.get(`${slot.closest('[data-round]').dataset.round}:${slot.dataset.characterId}`);
      if (!previous) continue;
      for (const { element, animation } of animateCardAppearance(slot, previous, { landed: slot === this.landedCharacter })) {
        this.trackAnimation(element, animation, waitForTurn);
      }
    }
    this.landedCharacter = null;
  }

  animateVisibility(entering, exits) {
    if (this.previewValue || this.motion.matches) return;
    this.positionAnimations ||= new Map();
    for (const snapshot of exits?.values() || []) {
      for (const { element, animation } of animateExit(snapshot, this.trackTarget, this.scrollLeft).animations) {
        this.trackAnimation(element, animation, false);
      }
    }
    if (this.positioned) return;
    const ids = new Set(entering.map(slot => slot.dataset.characterId));
    if (!ids.size) return;
    for (const slot of this.trackTarget.querySelectorAll('.character-list-2__slot:not(.is-hidden)')) {
      if (!ids.has(slot.dataset.characterId) || !this.visibleSlot(slot)) continue;
      if (slot.hasAttribute('data-character-list-2-reveal-phase')) continue;
      for (const { element, animation } of animateEntry(slot)) this.trackAnimation(element, animation, false);
    }
  }

  visibleSlot(slot) {
    const left = offsetInTrack(slot, this.trackTarget) - this.scrollLeft;
    return left + slot.offsetWidth >= 0 && left <= this.viewportTarget.clientWidth;
  }

  trackAnimation(element, animation, waitForTurn) {
    // Another live morph can arrive while the round banner is still showing.
    // Retarget that held layout without releasing it ahead of turn centering.
    if (waitForTurn || this.centerRequest?.phase === 'banner') animation.pause();
    this.positionAnimations.set(element, animation);
    animation.finished.then(() => {
      if (this.positionAnimations.get(element) !== animation) return;
      this.positionAnimations.delete(element);
      animation.cancel();
      this.updateRoundContext();
    }).catch(() => {});
  }

  playPositionAnimations() {
    for (const animation of this.positionAnimations?.values() || []) {
      if (animation.playState === 'paused') animation.play();
    }
  }

  cancelPositionAnimations() {
    for (const animation of this.positionAnimations?.values() || []) animation.cancel();
    this.positionAnimations?.clear();
  }

  measureRound() {
    const sample = document.createElement('div');
    sample.className = 'character-list-2__round';
    const divider = this.createDivider(this.roundValue + 1);
    const card = this.template(this.records[0]).cloneNode(true);
    sample.append(divider, card);
    // Do not clear overflowing content while measuring: the browser would clamp
    // scrollLeft and visibly jump before the replacement window can be mounted.
    this.futureTarget.append(sample);
    const gap = parseFloat(getComputedStyle(sample).columnGap) || 0;
    this.stride = card.offsetWidth + gap;
    this.dividerWidth = divider.offsetWidth;
    this.cardStart = offsetInTrack(card, this.trackTarget) - offsetInTrack(sample, this.trackTarget);
    this.roundWidth = sample.offsetWidth + (this.records.length - 1) * this.stride;
    sample.remove();
  }

  template(character) {
    const id = character.dataset.characterId;
    if (!this.templates.has(id)) this.templates.set(id, presentationCard(character));
    return this.templates.get(id);
  }

  createDivider(round) {
    const divider = document.createElement('div');
    divider.className = 'character-list-2__round-divider';
    const label = document.createElement('span');
    label.textContent = `Round ${round}`;
    divider.append(label);
    return divider;
  }

  spacer(width, key) {
    const spacer = this.spacers.get(key) || document.createElement('div');
    this.spacers.set(key, spacer);
    spacer.className = 'character-list-2__spacer';
    spacer.style.cssText = `flex: 0 0 ${Math.max(0, width)}px; width: ${Math.max(0, width)}px`;
    spacer.setAttribute('aria-hidden', 'true');
    return spacer;
  }

  roundGroup(slice) {
    let group = this.groups.get(slice.round);
    if (!group) {
      group = document.createElement('div');
      group.className = 'character-list-2__round';
      group.dataset.round = slice.round;
      group.setAttribute('aria-hidden', 'true');
      group.inert = true;
      this.groups.set(slice.round, group);
    }
    const key = `${this.revision}:${slice.first}:${slice.last}:${this.roundWidth}`;
    if (group.windowKey !== key) {
      group.windowKey = key;
      group.style.cssText = `position: relative; flex: 0 0 ${this.roundWidth}px; width: ${this.roundWidth}px; padding: 0`;
      const divider = group.firstElementChild || this.createDivider(slice.round);
      divider.style.cssText = `position: absolute; left: 0; top: calc(-1 * var(--character-list-2-padding)); width: ${this.dividerWidth}px`;
      const existing = new Map([...group.querySelectorAll('.character-list-2__slot')]
        .map(card => [card.dataset.characterId, card]));
      const cards = this.records.slice(slice.first, slice.last + 1).map((record, index) => {
        const template = this.template(record);
        const content = template.innerHTML;
        let card = existing.get(record.dataset.characterId);
        if (!card || card.presentationContent !== content) {
          card = template.cloneNode(true);
          card.presentationContent = content;
        }
        card.classList.toggle('is-muted', slice.round !== nextOccurrenceRound(slice.first + index, this.activeIndex, this.roundValue));
        card.style.position = 'absolute';
        card.style.left = `${this.cardStart + (slice.first + index) * this.stride}px`;
        return card;
      });
      syncChildren(group, [divider, ...cards]);
    }
    return group;
  }

  renderWindow(desiredScrollLeft = this.scrollLeft) {
    if (!this.combatValue || !this.roundWidth) return;
    // One card of overscan covers entering edges without cloning whole rosters.
    const start = Math.max(0, desiredScrollLeft - this.stride);
    const end = desiredScrollLeft + this.viewportTarget.clientWidth + this.stride;
    const sourceEnd = this.prefixWidth + this.sourceWidth;
    const slices = visibleRoundSlices({ start, end, currentRound: this.roundValue, originRound: this.originRound,
      leadingWidth: this.leadingWidth, sourceWidth: this.sourceWidth, roundWidth: this.roundWidth,
      cardStart: this.cardStart, stride: this.stride, count: this.records.length });
    const key = JSON.stringify([this.revision, slices, Math.ceil(end / this.roundWidth)]);
    if (key === this.windowKey) return;
    this.windowKey = key;
    const retained = new Set(slices.map(slice => slice.round));
    for (const round of this.groups.keys()) if (!retained.has(round)) this.groups.delete(round);
    const past = slices.filter(slice => slice.round < this.roundValue);
    const future = slices.filter(slice => slice.round > this.roundValue);
    const pastChildren = [];
    let cursor = 0;
    for (const slice of past) {
      if (slice.left > cursor) pastChildren.push(this.spacer(slice.left - cursor, 'past-leading'));
      pastChildren.push(this.roundGroup(slice));
      cursor = slice.left + this.roundWidth;
    }
    if (cursor < this.prefixWidth) pastChildren.push(this.spacer(this.prefixWidth - cursor, 'past-trailing'));
    const futureChildren = [];
    cursor = sourceEnd;
    for (const slice of future) {
      if (slice.left > cursor) futureChildren.push(this.spacer(slice.left - cursor, 'future-leading'));
      futureChildren.push(this.roundGroup(slice));
      cursor = slice.left + this.roundWidth;
    }
    // A lightweight tail permits optional keyboard navigation; no offscreen
    // portraits, image decoders, controllers or condition timers are created.
    const tail = Math.max(sourceEnd, end) + this.viewportTarget.clientWidth;
    if (cursor < tail) futureChildren.push(this.spacer(tail - cursor, 'future-trailing'));
    syncChildren(this.pastTarget, pastChildren);
    syncChildren(this.futureTarget, futureChildren);
    this.dispatch('window-updated');
  }

  scroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.renderWindow();
      this.updateRoundContext();
      this.updateReturnButton();
    });
  }

  captureAnchor() {
    if (!this.hasViewportTarget || !this.hasTrackTarget) return null;
    const left = this.scrollLeft;
    const slot = [...this.trackTarget.querySelectorAll('.character-list-2__slot:not(.is-hidden)')]
      .find(element => offsetInTrack(element, this.trackTarget) + element.offsetWidth > left);
    if (!slot) return null;
    return { id: slot.dataset.characterId,
      round: Number(slot.closest('[data-round]')?.dataset.round ?? this.roundValue),
      index: this.characters.findIndex(character => character.dataset.characterId === slot.dataset.characterId),
      offset: offsetInTrack(slot, this.trackTarget) - left };
  }

  anchorPosition(anchor) {
    if (!anchor) return null;
    const records = this.records || this.characters;
    let index = records.findIndex(character => character.dataset.characterId === anchor.id);
    if (index < 0) index = Math.min(anchor.index || 0, records.length - 1);
    if (index < 0) return null;
    if (!this.combatValue || !this.roundWidth || anchor.round === this.roundValue) {
      const slot = records[index];
      const relative = offsetInTrack(slot, this.trackTarget) - offsetInTrack(this.sourceTarget, this.trackTarget);
      return Math.max(0, (this.prefixWidth || 0) + relative - anchor.offset);
    }
    const round = Math.max(this.originRound, anchor.round);
    const left = round < this.roundValue
      ? this.leadingWidth + (round - this.originRound) * this.roundWidth
      : this.prefixWidth + this.sourceWidth + (round - this.roundValue - 1) * this.roundWidth;
    return Math.max(0, left + this.cardStart + index * this.stride - anchor.offset);
  }

  restoreAnchor(anchor) {
    const left = this.anchorPosition(anchor);
    if (left === null) return;
    this.renderWindow(left);
    this.scrollLeft = left;
  }

  async centerActive(initial = false) {
    this.stopScrolling();
    if (!this.combatValue) {
      this.centerRequest = null;
      this.pendingCenter = false;
      this.playPositionAnimations();
      return;
    }
    const request = this.centerRequest = { phase: 'banner' };
    const instant = initial || this.previewValue || this.motion.matches;
    if (!instant) await waitForRoundAnimation(this.element);
    if (!this.connected || this.centerRequest !== request) return;
    request.phase = 'turn';
    this.playPositionAnimations();
    if (!this.combatValue) {
      this.centerRequest = null;
      this.pendingCenter = false;
      return;
    }
    if (!instant) {
      // A content refresh may replace animations during this wait. Re-read the
      // tracked set after each completion so centering never outruns the handoff.
      while (this.connected && this.centerRequest === request && this.positionAnimations?.size) {
        await Promise.allSettled([...this.positionAnimations.values()].map(animation => animation.finished));
      }
    }
    if (!this.connected || this.centerRequest !== request) return;
    this.centerRequest = null;
    if (!this.combatValue) {
      this.pendingCenter = false;
      return;
    }
    if (this.positioned) {
      this.pendingCenter = true;
      return;
    }
    this.pendingCenter = false;
    const character = this.activeCharacter;
    if (!character || character.hasAttribute('data-character-list-2-reveal-phase')) return;
    this.scrollToCharacter(character, instant ? 'instant' : 'smooth', { timed: !instant });
  }

  returnToTurn() { this.centerActive(); }

  interact() {
    this.stopScrolling();
    this.playPositionAnimations();
    this.centerRequest = null;
    this.pendingCenter = false;
  }

  layoutChanged() {
    if (!this.connected) return;
    // A focus update can arrive while the turn camera is still moving. Freeze
    // the rail origin before positioning the card outside its scroll viewport.
    if (this.positioned && this.cancelScroll) {
      this.stopScrolling();
      this.pendingCenter = true;
    }
    if (!this.positioned && this.pendingCenter) this.centerActive();
    this.refresh();
  }

  centerReveal(event) {
    const { character, behavior } = event.detail;
    if (!this.connected || !character?.isConnected) return;
    this.scrollToCharacter(character, this.previewValue || this.motion.matches ? 'instant' : behavior);
  }

  revealSpaceStarted() {
    this.interact();
    this.cancelPositionAnimations();
  }

  revealSpaceGrown() {
    // Revealing cards are excluded from the roster key, but their completed
    // placeholder changes the source width and every later round's offset.
    this.stateKey = null;
    this.refresh();
  }

  beforeRevealLanding(event) {
    this.anchor = this.captureAnchor();
    this.beforePositions = this.capturePositions();
    this.beforeAppearances = this.captureAppearances() || new Map();
    const character = event.detail.character;
    this.beforeAppearances.set(this.roundValue + ':' + character.dataset.characterId, captureCardAppearance(character));
  }

  revealFinished(event) {
    if (event.detail.landed) this.landedCharacter = event.detail.character;
    this.refresh();
  }

  scrollToCharacter(character, behavior, { timed = false } = {}) {
    this.stopScrolling();
    const card = character.querySelector('.character-list-2__card');
    const center = offsetInTrack(character, this.trackTarget) + card.offsetLeft + card.offsetWidth / 2;
    const padding = parseFloat(getComputedStyle(this.viewportTarget).paddingLeft) || 0;
    const screenCenter = (this.element.clientWidth || this.viewportTarget.clientWidth) / 2 - (this.viewportTarget.offsetLeft || 0);
    const left = Math.max(0, center + padding - screenCenter);
    if (this.positioned) {
      this.renderWindow(left);
      this.scrollLeft = left;
      this.dispatch('updated');
      return;
    }
    if (timed && Math.abs(left - this.scrollLeft) > 0.5) {
      this.cancelScroll = animateScroll(this.scrollLeft, left, value => {
        this.renderWindow(value);
        this.scrollLeft = value;
        this.updateRoundContext();
        this.updateReturnButton();
        if (value === left) this.cancelScroll = null;
      });
    } else {
      this.viewportTarget.scrollTo({ left, behavior });
    }
  }

  stopScrolling() {
    this.cancelScroll?.();
    this.cancelScroll = null;
  }

  updateRoundContext() {
    if (!this.hasCurrentRoundTarget) return;
    const divider = this.sourceTarget.querySelector('.character-list-2__round-divider');
    let pinned = false;
    if (divider && this.combatValue && !this.positioned) {
      const transform = getComputedStyle(divider).transform;
      const translation = transform === 'none' ? 0 : new DOMMatrixReadOnly(transform).m41;
      const padding = parseFloat(getComputedStyle(this.viewportTarget).paddingLeft) || 0;
      pinned = offsetInTrack(divider, this.trackTarget) + translation + padding < this.scrollLeft;
    }
    this.currentRoundTarget.hidden = !pinned;
    divider?.classList.toggle('is-docked', pinned);
  }

  updateReturnButton() {
    if (!this.hasReturnToTurnTarget) return;
    const active = this.activeCharacter;
    const left = active && offsetInTrack(active, this.trackTarget);
    this.returnToTurnTarget.hidden = this.positioned || !this.combatValue || !active ||
      (left >= this.scrollLeft && left + active.offsetWidth <= this.scrollLeft + this.viewportTarget.clientWidth);
  }

  keydown(event) {
    if (event.target !== this.viewportTarget || !['ArrowLeft', 'ArrowRight', 'Home'].includes(event.key)) return;
    event.preventDefault();
    if (event.key === 'Home') this.returnToTurn();
    else {
      this.interact();
      this.viewportTarget.scrollBy({ left: (event.key === 'ArrowRight' ? 1 : -1) * this.viewportTarget.clientWidth * 0.65,
        behavior: this.motion.matches ? 'instant' : 'smooth' });
    }
  }

  clear() {
    this.stopScrolling();
    cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.scrollFrame);
    this.morphRequest = null;
    this.centerRequest = null;
    this.pendingCenter = false;
    if (this.hasFutureTarget) this.futureTarget.replaceChildren();
    if (this.hasPastTarget) this.pastTarget.replaceChildren();
    this.cancelPositionAnimations();
    this.beforePositions = null;
    this.beforeAppearances = null;
    this.beforeVisible = null;
    this.exits = null;
    this.landedCharacter = null;
    this.spacers?.clear();
    this.groups?.clear();
    this.templates?.clear();
    this.stateKey = undefined;
    this.windowKey = null;
    this.roundWidth = 0;
  }

  disconnect() {
    this.connected = false;
    this.motion?.removeEventListener('change', this.motionChanged);
    this.sourceObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.clear();
  }
}
